import { BuildRightClickDropDownAction } from "../utils/BuildRightClickDropDownAction";
import {
  enumCalcCode,
  enumCellObjectSubtypes,
  enumFieldType,
  enumHyperLinkType,
  enumObjClassNames
} from "../constants";
import { QFormulaRule } from "./QFormulaRule";
import { QXMLObject } from "./QXMLObject";
import { StringToBool } from "../utils/StringToBool.js";

/**
 * @typedef {import('./QObjDBClass').QObjDBClass} QObjDBClass
 */

export class QObjDBFieldDef extends QXMLObject {
  constructor() {
    super();
    this.fFieldName = null;
    this.fFieldID = null;
    this.fUID = null;

    this.fReadOnly = false;
    this.fCalcCode = null;
    this.fCacheFieldDisplayName = null;
    this.fFieldType = null;
    this.fNumDecPlaces = null;
    this.fFormulaRule = null;

    this.fFieldLabel = null;
    this.fPostfix = null;
    this.fPrefix = null;
    this.fShowThousandsSep = true;
    this.fShowPercent = false;
    this.fBreakChars = null;
    this.fHyperlinkType = enumHyperLinkType.notLink;
    this.fDateFormat = 0;
    this.fShowLocal = false;

    this.fManualAdd = false;

    this.fSaveIndex = null;
    this.fCompressed = false;
    /**
     * @private
     * @type {(null | QObjDBClass)}
     */
    this.fClass = null;
    this.fAlsoReportsTo = false;
    this.fInternalOnly = false;

    this.fPlanningFormulaDef = null;

    this.fListDelimiter = null;
  }

  GetRightClickActionList(dataObj) {
    //function GetActionList(theField, dataObj)
    var theField = this;
    var cellActions = [];
    if (theField == null) return cellActions;

    var fieldType = theField.GetFieldType();
    if (fieldType === enumFieldType.image)
      cellActions.push(BuildRightClickDropDownAction("reloadpic", "arrows-rotate"));
    else if (fieldType === enumFieldType.object) return cellActions;
    else cellActions.push(BuildRightClickDropDownAction("docopy", "clipboard"));

    var hType = theField.GetHyperlinkType();
    switch (hType) {
      case enumHyperLinkType.email:
        cellActions.push(BuildRightClickDropDownAction("doemail", "envelope", null, true));
        break;
      case enumHyperLinkType.phone:
        cellActions.push(BuildRightClickDropDownAction("docall", "phone", null, true));
        break;
      case enumHyperLinkType.skype:
        cellActions.push(BuildRightClickDropDownAction("doskype", "user-play", null, true));
        break;
      case enumHyperLinkType.hyperlink:
        cellActions.push(
          BuildRightClickDropDownAction("dohyperlink", "arrow-up-right-from-square", null, true)
        );
        break;
      case enumHyperLinkType.file:
        cellActions.push(BuildRightClickDropDownAction("dofile", "file-export", null, true));
        break;
    }
    return cellActions;
  }

  InitField(fieldID, fieldName, theClass) {
    var fieldUID = theClass.GetNextUID();
    this.InitField2(fieldID, fieldName, theClass, fieldUID);
  }

  InitField2(fieldID, fieldName, theClass, fieldUID) {
    this.fFieldID = theClass.IsUsingFieldCarets() ? fieldID.replaceAll(" ", "^") : fieldID;
    this.fFieldName = fieldName;

    this.fFieldType = enumFieldType.text;
    this.fFieldLabel = "";
    this.fNumDecPlaces = 0;

    this.fUID = fieldUID;
    this.fClass = theClass;
    this.SetXMLElementName("qfield");
  }

  ClearFormulaCache() {
    if (this.fFormulaRule != null) this.fFormulaRule.ResetCache();
  }

  IsReserved() {
    var reserved = this.GetCalcCode() != null;
    if (!reserved) reserved = this.fFieldID == "ID";
    return reserved;
  }

  IsSerializable(allowId = false) {
    const calcCode = this.GetCalcCode();
    const isCalc = calcCode !== null;
    const isStatic = calcCode === enumCalcCode.static;
    const isId = this.fFieldID === "ID";

    return (!isCalc || isStatic) && (allowId || !isId);
  }

  GetPlanningDef() {
    return this.fPlanningFormulaDef;
  }

  SetPlanningFieldDef(planningFieldDef) {
    this.fPlanningFormulaDef = planningFieldDef;
  }

  SetManualAdd(manualAdd) {
    this.fManualAdd = manualAdd;
  }

  GetManualAdd() {
    return this.fManualAdd;
  }

  SetSaveIndex(saveIndex) {
    this.fSaveIndex = saveIndex;
  }

  GetSaveIndex() {
    return this.fSaveIndex;
  }

  GetCompressed() {
    return this.fCompressed;
  }

  SetCompressed(isCompressed) {
    this.fCompressed = isCompressed;
  }

  GetCalcCode() {
    return this.fCalcCode;
  }

  SetCalcCode(calcCode) {
    this.fCalcCode = calcCode;
  }

  IsCustomCalc() {
    var calcCode = this.GetCalcCode();
    return calcCode == enumCalcCode.customcalc;
  }

  SetTableRef(tableRef) {
    this.fTableRef = tableRef;
  }

  GetTableRef() {
    return this.fTableRef;
  }

  IsConcat() {
    if (this.IsCustomCalc()) {
      var r = this.GetFormulaRule();
      var calcType = r.GetFormulaType();
      return calcType == "concatenate";
    }

    return false;
  }

  IsList() {
    return this.GetFieldSubtype() === enumCellObjectSubtypes.list;
  }

  IsPickList() {
    return this.GetFieldSubtype() === enumCellObjectSubtypes.picklist;
  }

  IsConstrainedPickList() {
    return this.IsPickList() && this.GetPickType() === "constrained";
  }

  IsLookup() {
    if (this.IsCustomCalc()) {
      var r = this.GetFormulaRule();
      var calcType = r.GetFormulaType();
      return calcType == "lookup";
    }

    return false;
  }

  SetReadOnly(readOnly) {
    this.fReadOnly = readOnly;
  }

  FieldReadOnly() {
    return this.fReadOnly;
  }

  IsLockedField() {
    return this.GetFieldID() == "Locked";
  }

  IsPersistField() {
    return this.GetFieldID() == "Persist";
  }

  IsPositionField() {
    return this.GetParentClass().GetClassName() === enumObjClassNames.position;
  }

  SetFieldDisplayName(newName) {
    this.fFieldName = this.fClass.IsUsingFieldCarets() ? newName.replaceAll(" ", "^") : newName;
    this.fCacheFieldDisplayName = null;
    this.fClass.ResetIndex();
  }

  GetFieldDisplayName() {
    //Unlike the field label, this one is generated from the internal field name. This may not need to exist.
    if (this.fCacheFieldDisplayName != null) return this.fCacheFieldDisplayName;

    this.fCacheFieldDisplayName = this.fClass.IsUsingFieldCarets()
      ? this.fFieldName.replaceAll("^", " ")
      : this.fFieldName;
    return this.fCacheFieldDisplayName;
  }

  GetFieldName() {
    return this.fFieldName;
  }

  SetFieldName(fieldName) {
    this.fFieldName = fieldName;
  }

  GetFieldLabel() {
    return this.fFieldLabel;
  }

  SetFieldLabel(fieldLabel) {
    this.fFieldLabel = fieldLabel;
  }

  GetFieldType() {
    return this.fFieldType;
  }

  GetFieldSubtype() {
    return this.fFieldSubtype;
  }

  SetFieldSubtype(subType) {
    this.fFieldSubtype = subType;
  }

  SetFieldType(fieldType) {
    this.fFieldType = fieldType;
  }

  SetInternalOnly(internalOnly) {
    this.fInternalOnly = internalOnly;
  }

  GetInternalOnly() {
    return this.fInternalOnly;
  }

  GetNumDecPlaces() {
    return this.fNumDecPlaces;
  }

  SetNumDecPlaces(numDecPlaces) {
    this.fNumDecPlaces = numDecPlaces;
  }

  GetShowPercent() {
    return this.fShowPercent;
  }

  SetShowPercent(showPercent) {
    this.fShowPercent = showPercent;
  }

  GetShowThousandsSep() {
    return (
      this.fShowThousandsSep || this.fShowThousandsSep == null || this.fShowThousandsSep === ""
    );
  }

  SetShowThousandsSep(showThousandsSep) {
    this.fShowThousandsSep = showThousandsSep;
  }

  GetPrefix() {
    return this.fPrefix;
  }

  SetPrefix(prefix) {
    this.fPrefix = prefix;
  }

  GetPostfix() {
    return this.fPostfix;
  }

  SetPostfix(postfix) {
    this.fPostfix = postfix;
  }

  GetBreakChars() {
    return this.fBreakChars;
  }

  SetBreakChars(breakChars) {
    this.fBreakChars = breakChars;
  }

  GetDateFormat() {
    return this.fDateFormat;
  }

  SetDateFormat(dateFormat) {
    this.fDateFormat = dateFormat;
  }

  GetHyperlinkType() {
    return this.fHyperlinkType;
  }

  SetHyperlinkType(linkType) {
    this.fHyperlinkType = linkType;
  }

  GetShowLocal() {
    return this.fShowLocalTime;
  }

  SetShowLocal(showLocal) {
    this.fShowLocalTime = showLocal;
  }

  // picktype can be 'constrained' or 'unconstrained'. Not using a boolean here.
  // could be additional types added in the future, and want to give flexibility.
  SetPickType(pickType) {
    this.fPickType = pickType;
  }

  // this.fPickValues will be an array of values available for a picklist field
  SetPickValues(pickValues) {
    this.fPickValues = pickValues;
  }

  GetListDelimiter() {
    return this.fListDelimiter;
  }

  SetListDelimiter(delimiter) {
    this.fListDelimiter = delimiter;
  }

  GetPickType() {
    return this.fPickType;
  }

  GetPickValues() {
    return this.fPickValues;
  }

  GetFieldID() {
    return this.fFieldID;
  }

  GetFieldUID() {
    return this.fUID;
  }

  SetFieldID(fieldID) {
    this.fFieldID = this.fClass.IsUsingFieldCarets() ? fieldID.replaceAll(" ", "^") : fieldID;
    this.fClass.ResetIndex();
  }

  SetParentClass(parentClass) {
    this.fClass = parentClass;
  }

  GetParentClass() {
    return this.fClass;
  }

  GetDocRef() {
    const objClass = this.GetParentClass();
    const objDb = objClass?.GetObjDB();
    if (objDb == null) return null;
    return objDb.GetDocRef();
  }

  GetFieldClassName() {
    if (this.fClass == null) console.log("GetFieldClassName - this should never happen!");
    return this.fClass.GetClassName();
  }

  OutputToJSON(stream) {
    var fieldName = this.GetFieldName();
    var fieldID = this.GetFieldID();
    var fieldType = this.GetFieldType();
    var fieldSubtype = this.GetFieldSubtype();
    var manualAdd = this.GetManualAdd();

    var numDec = this.GetNumDecPlaces();
    var fieldLabel = this.GetFieldLabel();

    var showPercent = this.GetShowPercent();
    var thousandsSep = this.GetShowThousandsSep();
    var postfix = this.GetPostfix();
    var prefix = this.GetPrefix();
    var breakChars = this.GetBreakChars();
    var hyperLinkType = this.GetHyperlinkType();
    var dateFormat = this.GetDateFormat();
    var showLocal = this.GetShowLocal();

    var tableRef = this.GetTableRef();

    var calcCode = this.GetCalcCode();

    var pickType = this.GetPickType();
    var pickValues = this.GetPickValues();

    var defObj = {};
    defObj["fieldName"] = fieldName;
    defObj["fieldType"] = fieldType;
    defObj["fieldSubType"] = fieldSubtype;
    defObj["saveIndex"] = this.fSaveIndex;
    defObj["fieldID"] = fieldID;
    defObj["manualAdd"] = manualAdd;

    var fieldFormat = {};
    if (numDec != 0) fieldFormat["numDecimals"] = numDec;

    if (showPercent) fieldFormat["showPercent"] = showPercent;
    if (thousandsSep) fieldFormat["thousandsSep"] = thousandsSep;

    if (showLocal) fieldFormat["showLocal"] = showLocal;

    if (prefix && prefix.length > 0) fieldFormat["fieldPrefix"] = prefix;

    if (postfix && postfix.length > 0) fieldFormat["fieldPostfix"] = postfix;

    if (breakChars && breakChars.length > 0) fieldFormat["breakChars"] = breakChars;

    if (hyperLinkType != enumHyperLinkType.notLink) fieldFormat["hyperLinkType"] = hyperLinkType;

    if (fieldLabel && fieldLabel.length > 0) fieldFormat["fieldLabel"] = fieldLabel;

    if (dateFormat != 0) fieldFormat["dateFormat"] = dateFormat;

    var fieldFormula = {};

    if (calcCode) fieldFormula["calcCode"] = calcCode;

    if (tableRef != null)
      // enumerated table metadata belonging to this field
      fieldFormula["tableRef"] = tableRef;

    if (this.fFormulaRule) fieldFormula["formulaRule"] = this.fFormulaRule.OutputToJSON();

    var picklist = {};

    if (pickType != null) picklist["pickType"] = pickType;
    if (pickValues != null) picklist["pickValues"] = pickValues;

    defObj["pickList"] = picklist;
    defObj["fieldFormula"] = fieldFormula; //TODO: Probably don't want to attach this for everything...
    defObj["fieldFormat"] = fieldFormat;

    return defObj;
  }

  LoadFromJSON(json) {
    this.InitField2(json["fieldID"], json["fieldName"], this.fClass, this.fClass.GetNextUID());
    this.SetFieldType(json["fieldType"]);
    this.SetFieldSubtype(json["fieldSubType"]);
    this.SetManualAdd(json["manualAdd"]);
    this.fSaveIndex = json["saveIndex"];

    var fieldFormat = json["fieldFormat"];
    if (fieldFormat["numDecimals"]) this.SetNumDecPlaces(fieldFormat["numDecimals"]);

    if (fieldFormat["showPercent"]) this.SetShowPercent(fieldFormat["showPercent"]);

    if (fieldFormat["thousandsSep"]) this.SetShowThousandsSep(fieldFormat["thousandsSep"]);

    if (fieldFormat["showLocal"]) this.SetShowLocal(fieldFormat["showLocal"]);

    if (fieldFormat["fieldPrefix"]) this.SetPrefix(fieldFormat["fieldPrefix"]);

    if (fieldFormat["fieldPostfix"]) this.SetPostfix(fieldFormat["fieldPostfix"]);

    if (fieldFormat["breakChars"]) this.SetBreakChars(fieldFormat["breakChars"]);

    if (fieldFormat["hyperLinkType"]) this.SetHyperlinkType(fieldFormat["hyperLinkType"]);

    if (fieldFormat["fieldLabel"]) this.SetFieldLabel(fieldFormat["fieldLabel"]);

    if (fieldFormat["dateFormat"]) this.SetDateFormat(fieldFormat["dateFormat"]);

    var fieldFormula = json["fieldFormula"];

    if (fieldFormula["calcCode"]) this.SetCalcCode(fieldFormula["calcCode"]);

    if (fieldFormula["calcCode"]) this.SetCalcCode(fieldFormula["calcCode"]);

    if (fieldFormula["tableRef"]) this.SetTableRef(fieldFormula["tableRef"]);

    if (fieldFormula["formulaRule"]) {
      var formulaRule = new QFormulaRule();
      formulaRule.InitFormulaRule(null); // to do : old xml load calls init on QRULE , was it intentional for chart temp not to?
      formulaRule.LoadFromJSON(fieldFormula["formulaRule"]);
      this.SetFormulaRule(formulaRule);
    }

    var pickList = json["pickList"];
    if (pickList) {
      this.SetPickValues(pickList["pickValues"]);
      this.SetPickType(pickList["pickType"]);
    }
  }

  OutputSubElements(stream) {
    var fieldName = this.GetFieldName();
    var fieldID = this.GetFieldID();
    var fieldType = this.GetFieldType();
    var fieldSubtype = this.GetFieldSubtype();
    var numDec = this.GetNumDecPlaces();
    var fieldLabel = this.GetFieldLabel();

    var showPercent = this.GetShowPercent();
    var thousandsSep = this.GetShowThousandsSep();
    var postfix = this.GetPostfix();
    var prefix = this.GetPrefix();
    var breakChars = this.GetBreakChars();
    var hyperLinkType = this.GetHyperlinkType();
    var dateFormat = this.GetDateFormat();
    var showLocal = this.GetShowLocal();

    var tableRef = this.GetTableRef();

    var calcCode = this.GetCalcCode();

    var pickType = this.GetPickType();
    var pickValues = this.GetPickValues();

    this.OutputSimpleElement("fieldname", fieldName, stream);
    this.OutputSimpleElement("fieldtype", fieldType, stream);
    this.OutputSimpleElement("fieldsubtype", fieldSubtype, stream);
    this.OutputSimpleElement("saveindex", this.fSaveIndex, stream);
    //	this.OutputSimpleElement("compress", this.fCompressed, stream);

    var manualAdd = this.GetManualAdd();
    if (manualAdd) this.OutputSimpleElement("useradd", manualAdd, stream);

    if (numDec != 0) this.OutputSimpleElement("fieldnumdec", numDec, stream);
    this.OutputSimpleElement("fieldID", fieldID, stream);

    if (showPercent) this.OutputSimpleElement("fieldpercent", showPercent, stream);
    if (thousandsSep) this.OutputSimpleElement("fieldthousands", thousandsSep, stream);

    if (showLocal) this.OutputSimpleElement("showLocal", showLocal, stream);

    if (prefix && prefix.length > 0) this.OutputSimpleElement("fieldprefix", prefix, stream);
    if (postfix && postfix.length > 0) this.OutputSimpleElement("fieldpostfix", postfix, stream);

    if (breakChars && breakChars.length > 0)
      this.OutputSimpleElement("breakchars", breakChars, stream);

    if (hyperLinkType != enumHyperLinkType.notLink)
      this.OutputSimpleElement("hyperlink2", hyperLinkType, stream);

    if (fieldLabel && fieldLabel.length > 0)
      this.OutputSimpleElement("fieldLabel", fieldLabel, stream);

    if (dateFormat != 0) this.OutputSimpleElement("dateformat", dateFormat, stream);

    if (calcCode) this.OutputSimpleElement("calcCode", calcCode, stream);

    if (tableRef != null)
      // enumerated table metadata belonging to this field
      this.OutputSimpleElement("tableref", JSON.stringify(tableRef), stream);

    // Output picklist info when saving .oc5 file
    if (pickType != null) this.OutputSimpleElement("picktype", pickType, stream);
    if (pickValues != null)
      this.OutputSimpleElement("pickvalues", JSON.stringify(pickValues), stream);

    if (this.fFormulaRule) this.fFormulaRule.OutputToXML(stream);
  }

  LoadSubElements(objTree) {
    var fieldName = objTree.GetChildByName("fieldname");
    var fieldID = objTree.GetChildByName("fieldID");

    var saveIndex = objTree.GetChildByName("saveindex");
    //	var compressInfo = objTree.GetChildByName("compress");

    var fieldNameVal = fieldName.GetValue();

    var fieldIDVal;
    if (fieldID == null) fieldIDVal = fieldNameVal;
    else fieldIDVal = fieldID.GetValue();

    var fieldType = objTree.GetChildByName("fieldtype");
    var fieldSubtype = objTree.GetChildByName("fieldsubtype");
    var numDec = objTree.GetChildByName("fieldnumdec");

    var fieldUID = this.fClass.GetNextUID();
    this.InitField2(fieldIDVal, fieldNameVal, this.fClass, fieldUID);

    if (saveIndex != null) {
      var sIndex = saveIndex.GetValue();
      this.SetSaveIndex(parseInt(sIndex));
    }

    var manualAdd = objTree.GetChildByName("useradd");
    if (manualAdd) this.SetManualAdd(true);

    if (fieldType != null) this.SetFieldType(parseInt(fieldType.GetValue()));
    if (fieldSubtype != null) this.SetFieldSubtype(parseInt(fieldSubtype.GetValue()));

    if (numDec != null) this.SetNumDecPlaces(parseInt(numDec.GetValue()));

    var showPercent = objTree.GetChildByName("fieldpercent");
    var thousandsSep = objTree.GetChildByName("fieldthousands");
    if (showPercent != null) this.SetShowPercent(StringToBool(showPercent.GetValue()));
    if (thousandsSep != null) this.SetShowThousandsSep(StringToBool(thousandsSep.GetValue()));

    var prefix = objTree.GetChildByName("fieldprefix");
    var postfix = objTree.GetChildByName("fieldpostfix");
    if (prefix != null) this.SetPrefix(prefix.GetValue());
    if (postfix != null) this.SetPostfix(postfix.GetValue());

    var breakChars = objTree.GetChildByName("breakchars");
    if (breakChars != null) this.SetBreakChars(breakChars.GetValue());

    var hyperlinkType = objTree.GetChildByName("hyperlink2");
    if (hyperlinkType != null) this.SetHyperlinkType(parseInt(hyperlinkType.GetValue()));

    var dateFormat = objTree.GetChildByName("dateformat");
    if (dateFormat == null) dateFormat = 0;
    else dateFormat = parseInt(dateFormat.GetValue());
    this.SetDateFormat(dateFormat);

    var showLocal = objTree.GetChildByName("showLocal");
    if (showLocal) this.SetShowLocal(true);

    var fieldLabel = objTree.GetChildByName("fieldLabel");
    if (fieldLabel != null) this.SetFieldLabel(fieldLabel.GetValue());

    var calcCode = objTree.GetChildByName("calcCode");
    if (calcCode) this.SetCalcCode(parseInt(calcCode.GetValue()));

    // loading enumerated table info
    var tableRef = objTree.GetChildByName("tableref");
    if (tableRef != null) this.SetTableRef(JSON.parse(tableRef.GetValue()));

    // loading field picklist info
    var pickType = objTree.GetChildByName("picktype");
    if (pickType != null) this.SetPickType(pickType.GetValue());

    var pickValues = objTree.GetChildByName("pickvalues");
    if (pickValues != null) this.SetPickValues(JSON.parse(pickValues.GetValue()));
  }

  LoadChildElements(objTree) {
    var childList = objTree.GetChildOfTypeList("rule");
    var numRules = childList.length; // objTree.GetNumChildrenOfType("rule");
    if (numRules > 0) {
      var ruleInfo = childList[0];
      //    var ruleInfo = objTree.GetIndexChildOfType("rule",0);
      var aRule = new QFormulaRule();
      aRule.InitFormulaRule(null);
      aRule.LoadFromXML(ruleInfo);
      this.fFormulaRule = aRule;
      if (this.GetCalcCode() == null) this.SetCalcCode(enumCalcCode.customcalc);
    } else if (this.GetCalcCode() == enumCalcCode.customcalc) this.SetCalcCode(null);
  }

  SetFormulaRule(theRule) {
    this.fFormulaRule = theRule;
  }

  GetFormulaRule() {
    return this.fFormulaRule;
  }
}
